import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, setAuthToken } from "../api";
import { useApp } from "../context/AppContext";
import { Loader2 } from "lucide-react";

/**
 * Landing route for Sign in with Google.
 * The backend redirects here with our session JWT in the URL fragment
 * (#token=...) so it never appears in server logs. We store it, load the
 * user profile and enter the app. Mirrors AppleCallbackPage.
 */
export default function GoogleCallbackPage() {
  const navigate = useNavigate();
  const { login, t } = useApp();
  const ranRef = useRef(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    const hash = window.location.hash || "";
    const match = hash.match(/token=([^&]+)/);
    const token = match ? decodeURIComponent(match[1]) : null;
    // Remove the token from the address bar / history immediately
    window.history.replaceState({}, "", "/auth/google");
    if (!token) {
      setFailed(true);
      setTimeout(() => navigate("/", { replace: true }), 1500);
      return;
    }
    (async () => {
      try {
        setAuthToken(token);
        const { data: user } = await api.get("/auth/me");
        login(token, user);
        navigate("/messages", { replace: true });
      } catch (e) {
        setAuthToken(null);
        setFailed(true);
        setTimeout(() => navigate("/", { replace: true }), 1500);
      }
    })();
  }, [login, navigate]);

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-3" data-testid="google-callback-page">
      {failed ? (
        <p className="text-sm text-muted-foreground" data-testid="google-callback-error">{t("google_login_failed")}</p>
      ) : (
        <>
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">{t("google_signing_in")}</p>
        </>
      )}
    </div>
  );
}
